/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import { func, shape, number } from 'prop-types'
import Dropdown from '@groovehq/internal-design-system/lib/components/Dropdown/Dropdown'
import Button from '@groovehq/internal-design-system/lib/components/Button/Button'
import { text } from '@groovehq/internal-design-system/lib/styles/elements'
import { Trash } from '@groovehq/internal-design-system/lib/assets/icons'
import { selectAgentsSortedByName } from 'selectors/agents/base'
import { selectGroupsSortedByName } from 'selectors/app/groups'
import { selectMailboxesIncludingInaccessible } from 'selectors/mailboxes'
import MessageTemplateEditor from 'subapps/settings/components/MessageTemplateEditor'
import { reverseHashInt } from 'util/scatterSwap'
import { useWatch } from 'react-hook-form'
import { omit } from 'util/objects'

import RuleValue from '../RuleValue'
import DropdownMenu from '../DropdownMenu'
import { actionVariableTypes } from './data'
import { styles as conditionStyles } from '../Condition/Condition.styles'

const selectValue = (all, key) => {
  const selected = all.find(item => item.value === key) || {
    name: '',
  }
  return selected.name
}

const buildPossibleOptions = (
  values,
  parameterKey,
  agents,
  groups,
  mailboxes
) => {
  let possibleOptionsArray

  const {
    valueType,
    unassigned,
    anyone,
    currentUser,
    options: possibleOptions,
  } =
    values[parameterKey] || {}

  if (valueType === 'Group') {
    possibleOptionsArray = []
    if (unassigned) {
      possibleOptionsArray.push({ name: 'Unassigned', value: '0' })
    }
    groups.forEach(m => {
      possibleOptionsArray.push({
        name: m.name,
        value: reverseHashInt(m.id).toString(),
      })
    })
  } else if (valueType === 'Mailbox') {
    possibleOptionsArray = []
    mailboxes.forEach(m => {
      possibleOptionsArray.push({
        name: m.name,
        value: reverseHashInt(m.id).toString(),
      })
    })
  } else if (valueType === 'Agent') {
    possibleOptionsArray = []
    if (anyone) {
      possibleOptionsArray.push({ name: 'Anyone', value: '0' })
    }
    if (currentUser) {
      possibleOptionsArray.push({ name: 'Current User', value: '-1' })
    }
    if (unassigned) {
      possibleOptionsArray.push({ name: 'Unassigned', value: '0' })
    }
    agents.forEach(m => {
      possibleOptionsArray.push({
        name: m.name,
        value: reverseHashInt(m.id).toString(),
      })
    })
  } else {
    possibleOptionsArray = possibleOptions
  }

  return possibleOptionsArray
}

const Action = ({
  variables,
  onRemoveAction,
  onInput,
  onBlur,
  onActionChange,
  index,
  totalActions,
  name,
  control,
}) => {
  const action = useWatch({
    control,
    name,
  })

  const { parameters: allParameters = [], values = {} } = variables
  const [parameterKey, setParameterKey] = useState(action.type)
  const [initialParameterKey] = useState(parameterKey)

  const [valueKey, setValueKey] = useState(action.value)
  const [toAgentKey, setToAgentKey] = useState(action.to_agent)

  const agents = useSelector(selectAgentsSortedByName)
  const groups = useSelector(selectGroupsSortedByName)
  const mailboxes = useSelector(selectMailboxesIncludingInaccessible)

  const { dataType, paramType } = values[parameterKey] || {}

  const possibleOptionsArray = buildPossibleOptions(
    values,
    parameterKey,
    agents,
    groups,
    mailboxes
  )

  const possibleToAgentOptionsArray = buildPossibleOptions(
    values,
    'send_email_to_agent',
    agents,
    groups,
    mailboxes
  )

  const [value, setValue] = useState(possibleOptionsArray)
  const [toAgentValue, setToAgentValue] = useState(possibleToAgentOptionsArray)

  const selectedParameter = useMemo(
    () => selectValue(allParameters, parameterKey),
    [parameterKey, allParameters]
  )

  const selectedValue = useMemo(
    () => {
      return Array.isArray(value) ? selectValue(value, valueKey) : null
    },
    [value, valueKey]
  )

  const selectedToAgentValue = useMemo(
    () => {
      return Array.isArray(toAgentValue)
        ? selectValue(toAgentValue, toAgentKey)
        : null
    },
    [toAgentValue, toAgentKey]
  )

  // Handle select parameter
  const handleSelectParameter = useCallback(
    key => {
      setParameterKey(key)
      const newValue = values[key]?.options || []
      let lastValue = newValue[0] ? newValue[0].value : ''
      let lastToAgent = null

      const newPossibleOptionsArray = buildPossibleOptions(
        values,
        key,
        agents,
        groups,
        mailboxes
      )
      setValue(newPossibleOptionsArray)
      if (Array.isArray(newPossibleOptionsArray)) {
        lastValue = newPossibleOptionsArray[0].value
      }
      setValueKey(lastValue)

      if (key === 'send_email_to_agent') {
        setToAgentValue(newPossibleOptionsArray)
        if (Array.isArray(newPossibleOptionsArray)) {
          lastToAgent = newPossibleOptionsArray[0].value
        }
        setToAgentKey(lastToAgent)
      }

      // Pass select values to action
      onActionChange(
        {
          ...action,
          type: key,
          value: lastValue,
          to_agent: lastToAgent,
        },
        index
      )
    },
    [action, index, onActionChange, values, agents, groups, mailboxes]
  )

  // Handle select value
  const handleSelectValueKey = useCallback(
    key => {
      setValueKey(key)
      onActionChange(
        {
          ...action,
          value: key,
        },
        index
      )
    },
    [action, onActionChange, index]
  )

  const handleSelectToAgentKey = useCallback(
    key => {
      setToAgentKey(key)
      onActionChange(
        {
          ...action,
          to_agent: key,
        },
        index
      )
    },
    [action, onActionChange, index]
  )

  // Handle select value
  const handleDelete = useCallback(
    () => {
      onRemoveAction(action, index)
    },
    [action, onRemoveAction, index]
  )

  const handleInput = useCallback(
    e => {
      const newValue = e.target.value
      setValueKey(newValue)
      onInput(
        {
          ...action,
          value: newValue,
        },
        index
      )
    },
    [setValueKey, onInput, action, index]
  )

  const toAgentValueItem = useMemo(
    () => {
      return (
        <div css={conditionStyles.dropdownContainer}>
          <Dropdown
            overlay={<DropdownMenu data={toAgentValue} />}
            onSelect={handleSelectToAgentKey}
            selectedKey={toAgentKey}
          >
            <Dropdown.Button size="small" css={conditionStyles.dropdownBtn}>
              {selectedToAgentValue || <>&nbsp;</>}
            </Dropdown.Button>
          </Dropdown>
        </div>
      )
    },
    [handleSelectToAgentKey, toAgentValue, selectedToAgentValue, toAgentKey]
  )

  const parameters = useMemo(
    () => {
      return allParameters
        .filter(p => !p.deprecated || p.value === initialParameterKey)
        .map(p => omit(['deprecated'], p))
    },
    [allParameters, initialParameterKey]
  )

  return (
    <div css={conditionStyles.base}>
      <div css={conditionStyles.dropdowns}>
        <div css={conditionStyles.dropdownContainer} title={selectedValue}>
          <Dropdown
            overlay={<DropdownMenu data={parameters} />}
            onSelect={handleSelectParameter}
            selectedKey={parameterKey}
          >
            <Dropdown.Button size="small" css={conditionStyles.dropdownBtn}>
              {selectedParameter || 'Select an action...'}
            </Dropdown.Button>
          </Dropdown>
        </div>
        {parameterKey &&
          parameterKey === 'send_email_to_agent' &&
          toAgentValueItem}
        {parameterKey && (
          <RuleValue
            value={value}
            onDropdownSelect={handleSelectValueKey}
            selectedValue={selectedValue}
            onInputBlur={onBlur}
            onInputChange={handleInput}
            valueKey={valueKey}
            dataType={dataType}
            paramType={paramType}
            name={name}
          />
        )}
        <div>
          {totalActions > 1 && (
            <Button
              type="tertiary"
              size="small"
              customIcon={<Trash />}
              css={text.styles.textMediumDark}
              onClick={handleDelete}
            />
          )}
        </div>
      </div>
      {/* Show editor on a new line */}
      {parameterKey &&
        dataType &&
        dataType === 'MessageTemplate' && (
          <MessageTemplateEditor
            // eslint-disable-next-line camelcase
            type="Rule"
            id={action?.rule_reply_template}
            entityType="ruleActionMessageTemplate"
            open={open}
            mode="managed"
            showPluginImageUpload
            showPluginSourceEditing
            showAddAttachment
            showPluginVariablesPlaceholder
            pluginVariablesPlaceholderTypes={actionVariableTypes}
            isFormInputSubjectHidden
            formInputSubjectName={`actions.${index}.messageTemplate.subject`}
            formInputBodyName={`actions.${index}.messageTemplate.body`}
            formInputAttachmentIdsName={`actions.${index}.messageTemplate.attachments`}
            formInputPendingUploadsName={`actions.${index}.messageTemplate.pendingUploads`}
            formInputBodyLabel={null}
          />
        )}
      <div className="operator">AND</div>
    </div>
  )
}

Action.propTypes = {
  variables: shape({}),
  onRemoveAction: func,
  onInput: func,
  onBlur: func,
  onActionChange: func,
  totalActions: number,
}

Action.defaultProps = {
  variables: {},
  onRemoveAction() {},
  action: {},
  onInput() {},
  onBlur() {},
  onActionChange() {},
  onEditorData() {},
  totalActions: 0,
}

export default React.memo(Action)
